import React, { useState, useEffect, useRef, useCallback } from "react";
import DOMPurify from "dompurify";
import "./ProjectModal.css";
import "./ImageSlider/Modal.css";
import "./ImageSlider/ImageSlider.css";

const ProjectModal = ({ showModal, closeModal, project }) => {
  const [showImageModal, setShowImageModal] = useState(false);
  const [modalImageUrl, setModalImageUrl] = useState("");
  const [currentIndex, setCurrentIndex] = useState(0);
  const [imagesLoaded, setImagesLoaded] = useState(false);

  const modalRef = useRef(null);
  const closeButtonRef = useRef(null);
  const previousActiveElement = useRef(null);

  // Reset slide index when project changes
  useEffect(() => {
    if (showModal) {
      setCurrentIndex(0);
      setShowImageModal(false);
      setModalImageUrl("");
    }
  }, [showModal, project]);

  // Preload images when modal opens
  useEffect(() => {
    if (showModal && project?.slides?.length > 0) {
      const loadImages = async () => {
        try {
          setImagesLoaded(false);
          const imagePromises = project.slides.map(slide => {
            return new Promise((resolve, reject) => {
              const img = new Image();
              img.src = slide.url;
              img.onload = () => resolve(slide.url);
              img.onerror = () => reject(new Error(`Failed to load image: ${slide.url}`));
            });
          });
          await Promise.all(imagePromises);
          setImagesLoaded(true);
        } catch (error) {
          console.error("Error preloading images:", error);
          setImagesLoaded(true);
        }
      };
      loadImages();
    }
  }, [showModal, project]);

  // Focus trap and body scroll lock for project modal
  useEffect(() => {
    if (showModal) {
      previousActiveElement.current = document.activeElement;
      document.body.style.overflow = "hidden";
      // Focus the close button after render
      setTimeout(() => closeButtonRef.current?.focus(), 0);
    }
    return () => {
      document.body.style.overflow = "";
    };
  }, [showModal]);

  // Restore focus when modal closes
  const handleClose = useCallback(() => {
    closeModal();
    setTimeout(() => previousActiveElement.current?.focus(), 0);
  }, [closeModal]);

  // Focus trap within modal
  const handleFocusTrap = useCallback((e) => {
    if (e.key !== "Tab" || !modalRef.current) return;

    const focusableElements = modalRef.current.querySelectorAll(
      'button, [href], input, select, textarea, [tabindex]:not([tabindex="-1"])'
    );
    if (focusableElements.length === 0) return;

    const firstElement = focusableElements[0];
    const lastElement = focusableElements[focusableElements.length - 1];

    if (e.shiftKey) {
      if (document.activeElement === firstElement) {
        e.preventDefault();
        lastElement.focus();
      }
    } else {
      if (document.activeElement === lastElement) {
        e.preventDefault();
        firstElement.focus();
      }
    }
  }, []);

  // Keyboard handling for project modal
  useEffect(() => {
    if (!showModal) return;

    const handleKeyDown = (e) => {
      if (e.key === "Escape") {
        if (showImageModal) {
          setShowImageModal(false);
          setModalImageUrl("");
        } else {
          handleClose();
        }
      }
      handleFocusTrap(e);
    };

    window.addEventListener("keydown", handleKeyDown);
    return () => window.removeEventListener("keydown", handleKeyDown);
  }, [showModal, showImageModal, handleClose, handleFocusTrap]);

  // Arrow key navigation for image modal
  useEffect(() => {
    if (!showImageModal || !project?.slides) return;

    const handleArrows = (e) => {
      if (e.key === "ArrowLeft") {
        const newIndex = currentIndex === 0 ? project.slides.length - 1 : currentIndex - 1;
        setCurrentIndex(newIndex);
        setModalImageUrl(project.slides[newIndex].url);
      } else if (e.key === "ArrowRight") {
        const newIndex = currentIndex === project.slides.length - 1 ? 0 : currentIndex + 1;
        setCurrentIndex(newIndex);
        setModalImageUrl(project.slides[newIndex].url);
      }
    };

    window.addEventListener("keydown", handleArrows);
    return () => window.removeEventListener("keydown", handleArrows);
  }, [showImageModal, currentIndex, project]);

  const openImageModal = (index) => {
    setCurrentIndex(index);
    setModalImageUrl(project.slides[index].url);
    setShowImageModal(true);
  };

  const closeImageModal = () => {
    setShowImageModal(false);
    setModalImageUrl("");
  };

  const goToPrevious = () => {
    const newIndex = currentIndex === 0 ? project.slides.length - 1 : currentIndex - 1;
    setCurrentIndex(newIndex);
    setModalImageUrl(project.slides[newIndex].url);
  };

  const goToNext = () => {
    const newIndex = currentIndex === project.slides.length - 1 ? 0 : currentIndex + 1;
    setCurrentIndex(newIndex);
    setModalImageUrl(project.slides[newIndex].url);
  };

  if (!showModal || !project) return null;

  return (
    <div
      className="modal-overlay"
      onClick={handleClose}
      role="dialog"
      aria-modal="true"
      aria-labelledby="project-modal-title"
      ref={modalRef}
    >
      <div className="modal-content" onClick={(e) => e.stopPropagation()}>
        <button
          className="modal-close"
          onClick={handleClose}
          ref={closeButtonRef}
          aria-label="Fermer la fenêtre"
        >
          &times;
        </button>
        <div className="container_modal">
          <h2 id="project-modal-title">{project.title}</h2>
          <div className="content_modal">
            <div className="image_modal">
              {project.slides && project.slides.length > 0 ? (
                <div style={{ position: 'relative' }}>
                  {imagesLoaded ? (
                    <>
                      <img
                        src={project.slides[currentIndex].url}
                        alt={project.slides[currentIndex].title}
                        onClick={() => openImageModal(currentIndex)}
                        style={{ cursor: "pointer" }}
                      />
                      <button
                        className="loupe"
                        onClick={() => openImageModal(currentIndex)}
                        aria-label="Agrandir l'image"
                      >
                        <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 512 512" aria-hidden="true">
                          <path d="M416 208c0 45.9-14.9 88.3-40 122.7L502.6 457.4c12.5 12.5 12.5 32.8 0 45.3s-32.8 12.5-45.3 0L330.7 376c-34.4 25.2-76.8 40-122.7 40C93.1 416 0 322.9 0 208S93.1 0 208 0S416 93.1 416 208zM184 296c0 13.3 10.7 24 24 24s24-10.7 24-24V232h64c13.3 0 24-10.7 24-24s-10.7-24-24-24H232V120c0-13.3-10.7-24-24-24s-24 10.7-24 24v64H120c-13.3 0-24 10.7-24 24s10.7 24 24 24h64v64z"/>
                        </svg>
                      </button>
                      <button className="slider-arrow left" onClick={goToPrevious} aria-label="Image pr&eacute;c&eacute;dente">
                        &#10094;
                      </button>
                      <button className="slider-arrow right" onClick={goToNext} aria-label="Image suivante">
                        &#10095;
                      </button>
                    </>
                  ) : (
                    <div className="loading-spinner" role="status">
                      <span>Chargement des images...</span>
                    </div>
                  )}
                </div>
              ) : (
                <div className="image-placeholder" role="img" aria-label="Aucune image disponible pour ce projet">
                  Aucune image disponible
                </div>
              )}
            </div>
            <div
              className="description_modal"
              dangerouslySetInnerHTML={{
                __html: DOMPurify.sanitize(project.content, {
                  ALLOWED_TAGS: ['p', 'h3', 'ul', 'li', 'a', 'span', 'div', 'br', 'strong', 'em', 'i'],
                  ALLOWED_ATTR: ['href', 'target', 'rel', 'class'],
                }),
              }}
            />
          </div>
          {project.techs && (
            <div className="tech-list" aria-label="Technologies utilis&eacute;es">
              {project.techs.map((tech, index) => (
                <img key={index} src={tech} alt="Tech Icon" />
              ))}
            </div>
          )}
        </div>
      </div>
      {showImageModal && (
        <div
          className="image-modal-overlay"
          onClick={closeImageModal}
          role="dialog"
          aria-modal="true"
          aria-label="Image agrandie"
        >
          <div className="image-modal-content" onClick={(e) => e.stopPropagation()}>
            <button
              className="image-modal-close"
              onClick={closeImageModal}
              aria-label="Fermer l'image"
            >
              &times;
            </button>
            <img src={modalImageUrl} alt={`${project.title}, en grand format`} className="image-modal-image" />
            <div className="image-modal-arrows">
              <button className="image-modal-arrow-left" onClick={goToPrevious} aria-label="Image pr&eacute;c&eacute;dente">
                &#10094;
              </button>
              <button className="image-modal-arrow-right" onClick={goToNext} aria-label="Image suivante">
                &#10095;
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default ProjectModal;
